import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Clock, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { useApi, waLink } from "@/lib/api";
import { fbEvent } from "@/lib/consent";
import { useSite } from "@/context/SiteContext";
import { Btn, PageHero, Reveal, SectionHeading } from "@/components/site/kit";
import { LeadForm } from "@/components/site/LeadForm";
import { FaqList } from "@/components/site/FaqList";

const Info = ({ icon: I, title, children, testId }) => (
  <div className="card-base flex gap-4 p-5" data-testid={testId}>
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#EEF6FC] text-ocean"><I className="h-5 w-5" /></span>
    <div className="text-sm"><p className="font-heading font-bold text-navy">{title}</p><div className="mt-0.5 text-body">{children}</div></div>
  </div>
);

export default function Contact() {
  useSeo({ title: "Contact Us", description: "Contact Own Brand Technologies in Faridabad for custom software, mobile apps and automation. Call, WhatsApp or send an enquiry." });
  const { settings: s } = useSite();
  const { data: c } = useApi("/content/contact");
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#quote") setTimeout(() => document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" }), 200);
  }, [hash]);

  return (
    <>
      <PageHero badge="Contact" title={c?.headline || "Let's Talk About"} highlight={c?.highlight || "Your Project"} subtitle={c?.intro} />
      <section id="quote" className="section scroll-mt-20 bg-page">
        <div className="container-x grid gap-10 lg:grid-cols-[1.15fr_0.85fr]">
          <Reveal>
            <div className="card-base p-6 sm:p-9">
              <h2 className="text-2xl font-bold">Send us a message</h2>
              <p className="mb-6 mt-2 text-sm text-body">We reply within one working day.</p>
              <LeadForm source="contact page" submitLabel="Send Message" testPrefix="contact-form" successMessage={c?.success_message} />
            </div>
          </Reveal>
          <div className="space-y-4">
            <Info icon={MapPin} title="Office address" testId="contact-address">{s?.address}</Info>
            <Info icon={Phone} title="Phone" testId="contact-phone"><a href={`tel:${s?.phone?.replace(/\s/g, "")}`} className="hover:text-ocean">{s?.phone}</a></Info>
            <Info icon={Mail} title="Email" testId="contact-email"><a href={`mailto:${s?.email}`} className="hover:text-ocean">{s?.email}</a></Info>
            <Info icon={Clock} title="Support hours" testId="contact-hours">{s?.support_hours}</Info>
            {(c?.extra_blocks || []).map((b, i) => <Info key={i} icon={MapPin} title={b.title} testId={`contact-extra-${i}`}><span className="whitespace-pre-line">{b.text}</span></Info>)}
            <Btn href={waLink(s?.whatsapp, "Hi, I'd like to discuss a project.")} onClick={() => fbEvent("Contact", { method: "whatsapp" })} className="w-full bg-[#25D366] text-white hover:bg-[#1EBE5B]" testId="contact-whatsapp-btn"><MessageCircle className="h-4 w-4" />Chat on WhatsApp</Btn>
            {s?.social_links && (
              <div className="flex flex-wrap gap-2 pt-2" data-testid="contact-social-links">
                {Object.entries(s.social_links).filter(([, v]) => v).map(([k, v]) => <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold capitalize text-navy hover:border-ocean hover:text-ocean">{k}</a>)}
              </div>
            )}
          </div>
        </div>
      </section>
      {s?.map_embed_url && (
        <section className="bg-white pb-4 pt-16">
          <div className="container-x">
            <iframe title="Office location map" src={s.map_embed_url} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="h-[380px] w-full rounded-2xl border border-line" data-testid="contact-map" />
          </div>
        </section>
      )}
      <section className="section bg-white">
        <div className="container-x grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeading badge="FAQ" title="Before you" highlight="reach out" />
          <FaqList category="contact" testId="contact-faq" />
        </div>
      </section>
    </>
  );
}
